import React, { useMemo, useState } from 'react'

import { EmptyState, Icon, Icons } from '@clientbase/clientbase-library'
import { Box, Skeleton, Tooltip, Typography } from '@mui/material'
import { format, parseISO } from 'date-fns'
import { getNotificationStatus } from 'templates/Notificacoes/Body/Sections/Overview/notification.constants'

import {
  formatInternationalPhone,
  isInternationalPhone,
  toBrazilianNationalDigits
} from 'utils/FormatPhone'

import { theme } from 'styles/theme'

import {
  InvoiceTimelineResponse,
  TimelineRecord
} from '../../types/invoiceTimeline'
import TimelineSummary from '../TimelineSummary/TimelineSummary'
import {
  filterRecords,
  TimelineFilter
} from '../TimelineSummary/TimelineSummary.utils'
import * as S from './InvoiceTimeline.styles'
import { resendLabelFor } from './resendOptions'

interface InvoiceTimelineProps {
  data: InvoiceTimelineResponse | null
  loading: boolean
  error: string | null
  /** Data de vencimento, que separa as duas faixas da cronologia. */
  dueDate?: string | null
  /** Início do rastreamento detalhado, para explicar cobranças legadas. */
  trackingStartedAt?: string
  /** Reenvio do envio daquela linha. */
  onResend?: (record: TimelineRecord) => void
}

/**
 * SC-10890: a data em que o rastreamento por evento entrou em produção.
 *
 * Cobrança anterior a isso tem `viewed`/`open_count` mas nenhum evento com hora —
 * e "sem histórico" sozinho lê como bug. Nomear a data transforma em fato do
 * sistema. Fica num lugar só para ser trocada no deploy.
 */
export const TRACKING_STARTED_AT = '10/09/2026'

const KIND_ICON: Record<string, Icons> = {
  notification: 'send',
  open: 'doneAll',
  reply: 'reply'
}

const MEDIUM_ICON: Record<string, Icons> = {
  'E-mail': 'faEnvelope',
  SMS: 'faBell',
  WhatsApp: 'faWhatsapp'
}

const formatMoment = (value?: string | null) =>
  value ? format(parseISO(value), "dd/MM/yyyy 'às' HH:mm") : '-'

/**
 * `due_date` chega como data pura ('2026-09-15'). `new Date('2026-09-15')` é
 * interpretado como UTC meia-noite e, em BRT (-03), volta um dia — a faixa
 * mostrava 14/09 para um vencimento em 15/09. parseISO trata data-só-dia como
 * local.
 */
const formatDay = (value?: string | null) =>
  value ? format(parseISO(value), 'dd/MM/yyyy') : '-'

const InvoiceTimeline = ({
  data,
  loading,
  error,
  dueDate,
  trackingStartedAt = TRACKING_STARTED_AT,
  onResend
}: InvoiceTimelineProps) => {
  const records = data?.records ?? []
  const effectiveDueDate = dueDate ?? data?.dueDate ?? null

  // Filtro dos cards do resumo: recorte de leitura sobre o que já está
  // carregado, sem ida à API. Estado local, nunca na URL — este componente vive
  // dentro de um modal, e mexer na URL daqui mudaria o endereço da página de
  // cobranças por trás do modal.
  const [filter, setFilter] = useState<TimelineFilter | null>(null)

  const visible = useMemo(
    () => filterRecords(records, filter),
    [records, filter]
  )

  /**
   * Ordena no front, e não só no backend: o componente não pode depender da
   * versão da API que está no ar para exibir a cronologia na ordem certa (uma
   * API mais antiga devolve decrescente, e a lista aparecia de trás para
   * frente).
   *
   * Crescente — mais antigo em cima — porque a página é lida de cima para
   * baixo. O desempate segue a ordem causal: envio, abertura, resposta.
   */
  const ordered = useMemo(() => {
    const kindWeight: Record<string, number> = {
      notification: 0,
      open: 1,
      reply: 2
    }

    return [...visible].sort((a, b) => {
      const timeA = a.occurredAt ? parseISO(a.occurredAt).getTime() : 0
      const timeB = b.occurredAt ? parseISO(b.occurredAt).getTime() : 0
      if (timeA !== timeB) return timeA - timeB
      return (kindWeight[a.kind] ?? 0) - (kindWeight[b.kind] ?? 0)
    })
  }, [visible])

  /**
   * Duas faixas em vez de uma linha de marco no meio da lista.
   *
   * É o que aposenta as tabs Pré/Pós-vencimento, que classificavam por STRING
   * do nome da campanha traduzido — lista hardcoded onde campanha nova caía
   * silenciosamente em "pós-vencimento". Aqui a faixa sai da data do evento.
   */
  const { before, after } = useMemo(() => {
    if (!effectiveDueDate) return { before: ordered, after: [] }

    // Fim do dia: um evento no próprio dia do vencimento é "antes de vencer".
    const dueTime = parseISO(effectiveDueDate).setHours(23, 59, 59, 999)

    return ordered.reduce<{
      before: TimelineRecord[]
      after: TimelineRecord[]
    }>(
      (acc, record) => {
        const at = record.occurredAt ? parseISO(record.occurredAt).getTime() : 0
        if (at > dueTime) acc.after.push(record)
        else acc.before.push(record)
        return acc
      },
      { before: [], after: [] }
    )
  }, [ordered, effectiveDueDate])

  if (loading) return <TimelineSkeleton />

  if (error) {
    return (
      <Box mt={2}>
        <EmptyState title="Não foi possível carregar" message={error} />
      </Box>
    )
  }

  if (records.length === 0) {
    return (
      <Box mt={2}>
        <EmptyState
          title="Nenhum histórico ainda"
          message="Os envios, as aberturas e as respostas do cliente aparecerão aqui."
        />
      </Box>
    )
  }

  return (
    <Box width="100%">
      {/* Os três números antes do trilho: com 24 envios, contar linha a linha
          para saber quantas falharam é trabalho que a tela deve poupar. */}
      <TimelineSummary
        records={records}
        statistics={data?.statistics}
        activeFilter={filter}
        onFilterChange={setFilter}
      />

      <LegacyNotice
        statistics={data?.statistics}
        trackingStartedAt={trackingStartedAt}
      />

      {/* Filtro sem resultado não cai no EmptyState geral: aquele diz "nenhum
          histórico ainda", que seria mentira aqui — o histórico existe, o
          recorte é que está vazio. O resumo acima continua na tela, e é por ele
          que se desfaz o filtro. */}
      {visible.length === 0 && (
        <Box paddingY="16px">
          <Typography variant="smLight" color={theme.palette.neutral[300]}>
            Nenhum registro para este filtro.
          </Typography>
        </Box>
      )}

      <S.List>
        {before.length > 0 && (
          <>
            <S.Band>
              <S.BandLabel>
                <Typography variant="xsBold" component="span" color="inherit">
                  Antes de vencer
                </Typography>
              </S.BandLabel>
              <S.BandLine />
            </S.Band>
            {before.map((record) => (
              <EventRow key={record.uuid} record={record} onResend={onResend} />
            ))}
          </>
        )}

        {after.length > 0 && (
          <>
            <S.Band>
              {/* A faixa carrega a data: é o enquadramento que as tabs davam,
                  sem precisar de uma linha só para o marco. */}
              <S.BandLabel tone="overdue">
                <Typography variant="xsBold" component="span" color="inherit">
                  {`Depois de vencer · ${formatDay(effectiveDueDate)}`}
                </Typography>
              </S.BandLabel>
              <S.BandLine tone="overdue" />
            </S.Band>
            {after.map((record) => (
              <EventRow key={record.uuid} record={record} onResend={onResend} />
            ))}
          </>
        )}
      </S.List>
    </Box>
  )
}

const EventRow = ({
  record,
  onResend
}: {
  record: TimelineRecord
  onResend?: (record: TimelineRecord) => void
}) => {
  const icon =
    record.kind === 'notification' && record.medium
      ? MEDIUM_ICON[record.medium] ?? KIND_ICON.notification
      : KIND_ICON[record.kind] ?? 'info'

  // Abertura usa success-mid; mint é cor de AÇÃO no sistema, não de estado
  // (regra Mint vs. Success do DESIGN.md).
  //
  // No ENVIO o ícone do canal carrega o estado do que FALHOU: é por ele que se
  // confere se a notificação saiu, e um e-mail que voltou tinha exatamente o
  // mesmo ícone cinza de um entregue.
  //
  // Só a falha ganha cor. Pintar o sucesso de verde deixaria a coluna inteira
  // verde numa cobrança com 24 envios, e o vermelho — que é o que pede ação —
  // deixaria de saltar. Cor onde muda a decisão, como manda o DESIGN.md.
  const iconColor =
    record.kind === 'open'
      ? '#1A803C'
      : record.kind === 'reply'
      ? '#366AB2'
      : statusOf(record).tone === 'error'
      ? '#990017'
      : theme.palette.neutral[300]

  const canResend =
    record.kind === 'notification' && record.resendable && !!onResend

  return (
    <S.Row>
      {/* Data primeiro: numa cronologia o "quando" é a chave de leitura. */}
      <S.DateCell>
        <Typography variant="xsLight" color={theme.palette.neutral[300]}>
          {formatMoment(record.occurredAt)}
        </Typography>
      </S.DateCell>

      <S.IconCell>
        <Tooltip arrow placement="top" title={channelTooltip(record)}>
          <Box display="inline-flex">
            <Icon icon={icon} colorSX={iconColor} width="15px" height="15px" />
          </Box>
        </Tooltip>
      </S.IconCell>

      <S.EventCell>
        <Typography variant="smBold">{titleOf(record)}</Typography>
        {detailOf(record) && (
          <Typography variant="xsLight" color={theme.palette.neutral[300]}>
            {/* Status e destinatário em texto corrido, no padrão do detalhe da
                cobrança. Falha ganha cor na palavra, não pílula: quatro chips
                vermelhos em coluna dominavam a seção. */}
            {record.kind === 'notification' && record.status && (
              <Box component="span" color={statusColorOf(record)}>
                {statusOf(record).label}
              </Box>
            )}
            {record.kind === 'notification' && record.status && detailOf(record)
              ? ' · '
              : ''}
            {detailOf(record)}
          </Typography>
        )}

        {record.kind === 'reply' && record.body && (
          <>
            <S.ReplyBubble>
              <Typography variant="smLight">{record.body}</Typography>
            </S.ReplyBubble>

            {/* A resposta pode ser sobre outra cobrança do mesmo cliente:
                dizer isso é o que mantém a informação utilizável. */}
            {record.correlation === 'inferred' && (
              <Box display="inline-flex" alignItems="center" gap="4px" mt="4px">
                <Icon
                  icon="info"
                  colorSX={theme.palette.neutral[300]}
                  width="13px"
                  height="13px"
                />
                <Typography
                  variant="xsLight"
                  color={theme.palette.neutral[300]}
                >
                  Pode se referir a outra cobrança
                </Typography>
              </Box>
            )}
          </>
        )}
      </S.EventCell>

      {/* Só o ícone: "Reenviar" e "Enviar agora" têm larguras diferentes, então
          o rótulo visível fazia os botões saírem de tamanhos diferentes entre as
          linhas. O texto vive no tooltip e no aria-label, que também nomeiam a
          notificação — quem usa teclado ou leitor de tela não perde nada. */}
      <S.ActionCell>
        {canResend && (
          <Tooltip
            arrow
            placement="left"
            title={`${resendLabelFor(record)} · ${titleOf(record)}`}
          >
            <S.ResendIconButton
              type="button"
              aria-label={`${resendLabelFor(record)} · ${titleOf(record)}`}
              onClick={() => onResend?.(record)}
            >
              <Icon
                icon="send"
                colorSX="currentColor"
                width="14px"
                height="14px"
              />
            </S.ResendIconButton>
          </Tooltip>
        )}
      </S.ActionCell>
    </S.Row>
  )
}

const LegacyNotice = ({
  statistics,
  trackingStartedAt
}: {
  statistics?: InvoiceTimelineResponse['statistics']
  trackingStartedAt: string
}) => {
  if (!statistics) return null

  const { openCount, hasDetailedOpenHistory } = statistics
  if (openCount === 0 || hasDetailedOpenHistory) return null

  return (
    <Box
      mb={1.5}
      p={1.25}
      display="flex"
      gap={1}
      alignItems="flex-start"
      border="1px solid #E3E6E3"
      borderRadius="5px"
      bgcolor="#F7FBFF"
    >
      <Icon icon="info" colorSX="#366AB2" width="18px" height="18px" />
      <Typography variant="smLight" color={theme.palette.neutral[400]}>
        Esta cobrança foi aberta pelo cliente. O registro detalhado de cada
        abertura começou em {trackingStartedAt} — aberturas anteriores não têm
        data e hora individuais.
      </Typography>
    </Box>
  )
}

const titleOf = (record: TimelineRecord): string => {
  if (record.kind === 'notification') {
    return record.campaignLabel || record.campaign || 'Notificação enviada'
  }
  if (record.kind === 'open') {
    return record.eventKind === 'click'
      ? 'Cliente clicou no link'
      : 'Fatura aberta pelo cliente'
  }
  return 'Resposta do cliente'
}

/**
 * Segunda linha: só o destinatário nos envios, porque o status virou chip. Em
 * abertura, o canal explica por onde ela veio.
 */
const detailOf = (record: TimelineRecord): string | null => {
  if (record.kind === 'notification') return contactOf(record)
  if (record.kind === 'reply') return contactOf(record)
  return (
    [record.channelLabel, contactOf(record)].filter(Boolean).join(' · ') || null
  )
}

/**
 * Telefone com DDI, DDD e espaço depois do parêntese: "+55 (41) 99999-9999".
 *
 * O `formatPhone` do repo (usado na Central de Notificações e no feed do
 * cliente) descarta o "55" e cola o número no parêntese. Aqui o DDI importa:
 * a timeline mostra o contato EXATO para onde o envio saiu, e é por ele que o
 * merchant confere se a cobrança foi para o número certo. Mudar o util
 * compartilhado mexeria em 8 outras telas, então a máscara é local.
 */
const formatTimelinePhone = (raw: string): string => {
  // Número estrangeiro ("15875796131" = +1 Canadá) não ganha o "+55": o
  // `contact_used` é o número exato para onde o envio saiu.
  if (isInternationalPhone(raw)) return formatInternationalPhone(raw)

  // Aceita com e sem DDI: o `contact_used` vem "5541999999999" nos envios de
  // WhatsApp e pode vir "41999999999" em base legada.
  const national = toBrazilianNationalDigits(raw)
  if (national.length !== 10 && national.length !== 11) return raw

  const ddd = national.slice(0, 2)
  const rest = national.slice(2)
  const prefixLength = rest.length === 9 ? 5 : 4

  return `+55 (${ddd}) ${rest.slice(0, prefixLength)}-${rest.slice(
    prefixLength
  )}`
}

/**
 * Telefone ganha a máscara com DDI; e-mail passa direto.
 */
const contactOf = (record: TimelineRecord): string | null => {
  const contact = record.contactUsed
  if (!contact) return null
  if (contact.includes('@')) return contact

  return formatTimelinePhone(contact)
}

/**
 * Reusa o mapa da Central de Notificações: o TOM de cada status já é
 * compartilhado entre aquela tela e o feed do cliente. Um terceiro mapa aqui
 * divergiria na primeira mudança.
 *
 * O RÓTULO é reescrito para o vocabulário de quem cobra: o mapa devolve
 * "Rejeitado"/"Spam", que é o vocabulário técnico do provedor. "Caiu em spam" é
 * a mesma informação em português de gente — e explica por que o cliente não
 * pagou.
 */
const PLAIN_STATUS_LABEL: Record<string, string> = {
  complaint: 'Caiu em spam',
  bounced: 'Caiu em spam',
  blacklisted: 'Contato bloqueado',
  muted_ruler: 'Silenciado',
  notifications_disabled: 'Notificações desativadas',
  mailbox_full: 'Caixa de entrada cheia',
  no_email: 'Sem e-mail cadastrado',
  no_phone: 'Sem telefone cadastrado',
  invalid_phone: 'Telefone inválido',
  undelivered: 'Não entregue'
}

const statusOf = (record: TimelineRecord) => {
  const resolved = getNotificationStatus(
    record.status || '',
    record.statusLabel
  )

  return {
    ...resolved,
    label: PLAIN_STATUS_LABEL[record.status || ''] || resolved.label
  }
}

/**
 * Cor só na palavra do status, sem fundo — o tom vem do mesmo mapa que a
 * Central de Notificações usa.
 *
 * `info` entra junto com `success` no verde: o único status com esse tom é
 * `opened` ("Aberto" no e-mail, "Lido" no WhatsApp), que é o melhor desfecho
 * possível de um envio. Em cinza ele lia como neutro, do mesmo tom de uma linha
 * sem status, quando na verdade é a notícia boa da linha.
 *
 * A distinção que importa nesta tela é binária: chegou ao cliente (verde) ou
 * não chegou (vermelho). `warning` fica em âmbar porque é o estado
 * intermediário — ainda pendente, nem sucesso nem falha.
 */
const statusColorOf = (record: TimelineRecord): string => {
  const { tone } = statusOf(record)

  if (tone === 'error') return '#990017'
  if (tone === 'success' || tone === 'info') return '#1A803C'
  if (tone === 'warning') return '#B27C30'
  return theme.palette.neutral[300]
}

/** O ícone da primeira coluna é só um símbolo; o tooltip o nomeia. */
const channelTooltip = (record: TimelineRecord): string => {
  if (record.kind === 'notification') return record.medium || 'Notificação'
  if (record.kind === 'open') return record.channelLabel || 'Abertura'
  return record.channelLabel || 'Resposta do cliente'
}

const TimelineSkeleton = () => (
  <Box width="100%">
    <Skeleton animation="wave" variant="rounded" width={220} height={17} />
    <Box mt={2} display="flex" flexDirection="column" gap={1.5}>
      {[0, 1, 2].map((row) => (
        <Box key={row} display="flex" alignItems="center" gap={1.5}>
          <Skeleton
            animation="wave"
            variant="circular"
            width={15}
            height={15}
          />
          <Box flexGrow={1}>
            <Skeleton
              animation="wave"
              variant="rounded"
              width="45%"
              height={15}
            />
          </Box>
          <Skeleton
            animation="wave"
            variant="rounded"
            width={130}
            height={13}
          />
        </Box>
      ))}
    </Box>
  </Box>
)

export default InvoiceTimeline
